import React from 'react'
import { cn } from '@/lib/utils'
import type { AgentRes } from '@/api/types/agent.type'

// 导入背景图片
import iconAgent1 from '@/assets/images/home/icon_agent_1.png'
import iconAgent2 from '@/assets/images/home/icon_agent_2.png'
import iconAgent3 from '@/assets/images/home/icon_agent_3.png'
import iconAgent4 from '@/assets/images/home/icon_agent_4.png'
import iconAgent5 from '@/assets/images/home/icon_agent_5.png'
import iconAgent6 from '@/assets/images/home/icon_agent_6.png'

// 组件属性接口
export interface PlatformPicksSectionProps {
  /** 标题 */
  title?: string
  /** 平台精选智能体数据 */
  agents: AgentRes[]
  /** 自定义类名 */
  className?: string
  /** 点击事件回调 */
  onAgentClick?: (agent: AgentRes) => void
}

// 背景图片数组
const backgroundImages = [
  iconAgent1,
  iconAgent2,
  iconAgent3,
  iconAgent4,
  iconAgent5,
  iconAgent6,
]

// 获取背景图片的函数
const getBackgroundImage = (index: number) => {
  return backgroundImages[index % backgroundImages.length]
}

// 单个智能体卡片组件
const PlatformAgentCard: React.FC<{
  agent: AgentRes
  index: number
  onClick?: () => void
}> = ({ agent, index, onClick }) => {
  const backgroundImage = getBackgroundImage(index)

  // 根据智能体属性生成tags
  const tags = [
    { text: '系统', theme: 'school' as const },
    { text: '管理员', theme: 'green' as const },
  ]

  return (
    <div
      className={cn(
        'relative cursor-pointer transition-all duration-300 ease-in-out',
        'hover:-translate-y-1 hover:shadow-lg',
        'h-[170px]',
        'rounded-lg border border-purple-light overflow-hidden'
      )}
      style={{
        background:
          'linear-gradient(70deg, rgba(255, 255, 255, 0.20) 90.15%, rgba(135, 82, 255, 0.20) 103.93%), #FFF',
      }}
      onClick={onClick}
    >
      {/* 背景图片 */}
      <div
        className="absolute inset-0 flex items-center justify-end pr-6"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: '138px 138px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right center',
        }}
      />

      {/* 内容区域 */}
      <div className="relative z-10 px-6 py-4 h-full flex flex-col">
        {/* 名字 */}
        <h3 className="text-text-primary font-bold text-xl leading-normal">
          {agent.name}
        </h3>

        {/* Tags */}
        <div
          className="flex items-center gap-1.5"
          style={{ marginTop: '13px', marginBottom: '13px' }}
        >
          {tags.map((tag, tagIndex) => (
            <span
              key={tagIndex}
              className="inline-block px-1.5 py-0.5 rounded text-xs transition-colors duration-200"
              style={{
                background: tag.theme === 'school' ? '#EEE6FF' : '#E6FFE7',
                color: tag.theme === 'school' ? '#8752FF' : '#29C392',
                fontSize: '9px',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: '"Noto Sans SC"',
              }}
            >
              {tag.text}
            </span>
          ))}
        </div>

        {/* 描述 */}
        <p
          className="text-text-secondary text-[14px] flex-1 font-['Noto_Sans_SC'] font-normal line-clamp-2"
          style={{
            lineHeight: '18px',
            maxHeight: '36px', // 2行 × 18px = 36px
          }}
        >
          {agent.description}
        </p>

        {/* 立即体验按钮 - 居下居左 */}
        <div className="flex items-center gap-1 group mt-auto">
          <span
            className="text-[#8752FF] text-sm font-normal leading-normal uppercase cursor-pointer"
            style={{
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: '"Noto Sans SC"',
            }}
            onClick={(e) => {
              e.stopPropagation()
              onClick?.()
            }}
          >
            立即体验
          </span>
          {/* 箭头图标 */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="13"
            viewBox="0 0 12 13"
            fill="none"
            className="transition-transform duration-200 group-hover:translate-x-1"
          >
            <path
              d="M2.12829 11.5C2.00481 11.5002 1.88404 11.4639 1.7812 11.3955C1.67837 11.3272 1.59809 11.2299 1.55048 11.116C1.50288 11.002 1.49008 10.8766 1.51372 10.7554C1.53736 10.6342 1.59636 10.5227 1.68329 10.435L5.60829 6.50001L1.68329 2.58001C1.56593 2.46265 1.5 2.30348 1.5 2.13751C1.5 1.97154 1.56593 1.81237 1.68329 1.69501C1.80065 1.57765 1.95982 1.51172 2.12579 1.51172C2.29176 1.51172 2.45093 1.57765 2.56829 1.69501L6.93329 6.06001C6.99149 6.11807 7.03767 6.18703 7.06918 6.26297C7.10069 6.3389 7.11691 6.4203 7.11691 6.50251C7.11691 6.58472 7.10069 6.66612 7.06918 6.74205C7.03767 6.81798 6.99149 6.88695 6.93329 6.94501L2.56829 11.305C2.51219 11.3659 2.4442 11.4146 2.36851 11.4481C2.29283 11.4817 2.21107 11.4993 2.12829 11.5Z"
              fill="#AB97D7"
            />
            <path
              d="M5.87829 11.5C5.75481 11.5002 5.63404 11.4639 5.5312 11.3955C5.42837 11.3272 5.34809 11.2299 5.30048 11.116C5.25288 11.002 5.24008 10.8766 5.26372 10.7554C5.28736 10.6342 5.34636 10.5227 5.43329 10.435L9.35829 6.50001L5.43329 2.58001C5.31593 2.46265 5.25 2.30348 5.25 2.13751C5.25 1.97154 5.31593 1.81237 5.43329 1.69501C5.55065 1.57765 5.70982 1.51172 5.87579 1.51172C6.04176 1.51172 6.20093 1.57765 6.31829 1.69501L10.6833 6.06001C10.7415 6.11807 10.7877 6.18703 10.8192 6.26297C10.8507 6.3389 10.8669 6.4203 10.8669 6.50251C10.8669 6.58472 10.8507 6.66612 10.8192 6.74205C10.7877 6.81798 10.7415 6.88695 10.6833 6.94501L6.31829 11.305C6.26219 11.3659 6.1942 11.4146 6.11851 11.4481C6.04283 11.4817 5.96107 11.4993 5.87829 11.5Z"
              fill="#AB97D7"
            />
          </svg>
        </div>
      </div>
    </div>
  )
}

const PlatformPicksSection: React.FC<PlatformPicksSectionProps> = ({
  title = '平台精选',
  agents,
  className,
  onAgentClick,
}) => {
  return (
    <div className={cn('mb-8', className)}>
      {/* 标题 */}
      <h1 className="text-2xl font-bold text-text-primary mb-6">{title}</h1>

      {/* 三列网格布局 */}
      <div className="w-[1200px] grid grid-cols-3 gap-10">
        {agents.map((agent, index) => (
          <PlatformAgentCard
            key={agent.id || agent.agentId}
            agent={agent}
            index={index}
            onClick={() => onAgentClick?.(agent)}
          />
        ))}
      </div>
    </div>
  )
}

export default PlatformPicksSection
